import {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import identity from 'lodash/identity';
import {Button, TabPane} from 'reactstrap';
import styled from 'styled-components';
import {IoWarningOutline} from 'react-icons/io5';
import withReactContent from 'sweetalert2-react-content';
import SwalBase from 'sweetalert2';

import {ChangeRequestConfig, PageDataType} from 'src/types';
import {ListingPage} from 'src/components';
import {deleteConfig, getChangeRequestConfigs} from 'src/api/changeRequestConfigs';
import {isSuccess, isError} from 'src/components/Administration/utils';

import {ActionMenu} from '../UserManagement/components';

import ChangeRequestConfigurationForm from './Details';

import 'src/components/Administration/administration.css';

const Swal = withReactContent(SwalBase);
const Container = styled.div`
  width: 100%;
  display: flex;
  flexDirection: row;
`;

const removeConfig = async (config:ChangeRequestConfig) => {
  const result = await deleteConfig(config.id);
  if (isSuccess(result)) {
    Swal.fire({
      title: 'Success!',
      html: 'Successfully deleted the configuration!',
      width: '45em',
      icon: 'success',
      showConfirmButton: true,
    });
  } else {
    if (isError(result)) {
      Swal.fire({
        title: 'Error',
        html: <span>{`${result.error.message} `}</span>,
        width: '45em',
        icon: 'error',
      });
    } else {
      Swal.fire({
        title: 'Error',
        html: <span>{`Error deleting config`}</span>,
        width: '45em',
        icon: 'error',
      });
    }
  }
};

export const ChangeRequestTab = ({setLoading}: {setLoading: (_: boolean) => void}) => {
  const [data, setData] = useState<ChangeRequestConfig[]>([]);
  const [pageData, setPageData] = useState<PageDataType>();
  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState<ChangeRequestConfig>();

  const configDeleteConfirmation = async (config: ChangeRequestConfig) => {
    const response = await Swal.fire({
      title: `Are you sure you want to delete this configuration?`,
      text: 'The configured repository will no longer generate diffs.',
      confirmButtonText: 'Delete',
      showCancelButton: true,
      customClass: 'WorkspaceAlert',
      iconHtml: <IoWarningOutline></IoWarningOutline>,
    });

    if (response.isConfirmed) {
      await removeConfig(config);
    }
  };

  const columns = useMemo<ColumnDef<ChangeRequestConfig>[]>(
      () => [
        {
          header: 'Repository Url',
          accessorKey: 'repositoryUrl',
          enableSorting: false,
        },
        {
          header: 'Branch Pattern',
          accessorKey: 'branchPattern',
          enableSorting: false,
        },
        {
          header: 'Comparison Workspace',
          accessorKey: 'workspaceName',
          enableSorting: false,
        },
        {
          header: 'Actions',
          accessorFn: identity,
          enableSorting: false,
          cell: ({getValue}: {getValue: () => ChangeRequestConfig}) => (
            <ActionMenu menuItems={[{
              label: 'Edit',
              action: () => {
                setIsOpen(true);
                setEditData(getValue());
              },
            }, {
              label: 'Delete',
              action: async () => {
                await configDeleteConfirmation(getValue());
                await fetchConfigs(pageData?.currentPage, pageData?.pageSize);
              },
            }]} />
          ),
        },
      ],
      [],
  );

  const fetchConfigs = useCallback(
      async (page, pageSize) => {
        setLoading(true);
        const result = await getChangeRequestConfigs({
          page,
          pageSize,
        });
        setLoading(false);
        if (result && result.data) {
          setData(result.data);
          setPageData(result.metaData.pageData);
        } else {
          setData([]);
          setPageData(undefined);
        }
      },
      [],
  );

  return (
    <TabPane tabId="3">
      <Container>
        <Button type='button' color='primary' style={{margin: '0 0 0 auto'}} onClick={() => {
          setEditData(undefined);
          setIsOpen(true);
        }}>
          Add Change Request
        </Button>
      </Container>
      <ListingPage
        columns={columns}
        data={data}
        pageData={pageData}
        fetchData={fetchConfigs}
      />
      <ChangeRequestConfigurationForm
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEditData(undefined);
          fetchConfigs(pageData?.currentPage, pageData?.pageSize);
        }
        }
        config={editData}
      ></ChangeRequestConfigurationForm>
    </TabPane>
  );
};
